import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Heading,
  Paragraph,
  Skeleton,
} from '@digdir/designsystemet-react';
import { useHrWorkEnvironmentOverviewQuery } from '../../hooks/queries/hr/useHrWorkEnvironmentOverviewQuery';
import { useMeasureDeadlinesQuery } from '../../hooks/queries/hr/useMeasureDeadlinesQuery';
import { questionLanguageFor } from '../FormSubmission/workEnvironmentFormView';
import { CoverageCard } from './HRWorkEnvironment/Overview/CoverageCard';
import { FixCards } from './HRWorkEnvironment/Overview/FixCards';
import { SetupCard } from './HRWorkEnvironment/Overview/SetupCard';
import { WorkInTeams } from './HRWorkEnvironment/Overview/WorkInTeams';
import classes from './HRWorkEnvironment/Overview/Overview.module.css';

/**
 * HR's overview of the psychosocial work environment in every team. Each card sums up one
 * thing, and links to a page where it is followed up.
 */
export const HRWorkEnvironment = (): ReactElement => {
  const { t, i18n } = useTranslation();
  const language = questionLanguageFor(i18n.language);
  const overview = useHrWorkEnvironmentOverviewQuery(language);
  const measures = useMeasureDeadlinesQuery(language);
  const isLoading = overview.isLoading || measures.isLoading;
  const isError = overview.isError || measures.isError;

  return (
    <div className={`${classes.page} ${classes.hub}`} data-size="md">
      <header className={classes.hero}>
        {overview.data && (
          <span className={`ds-label ${classes.eyebrow}`} data-size="sm">
            {t('hrOverview.eyebrow', {
              organization: overview.data.organizationName,
            })}
          </span>
        )}
        <Heading level={1} data-size="xl">
          {t('hrOverview.title')}
        </Heading>
        <Paragraph data-size="lg" className={classes.text}>
          {t('hrOverview.intro')}
        </Paragraph>
      </header>
      {isLoading && <Skeleton variant="rectangle" width="100%" height={320} />}
      {isError && (
        <Alert data-color="danger">
          <Paragraph>{t('hrOverview.error')}</Paragraph>
        </Alert>
      )}
      {overview.data && measures.data && (
        <>
          <div className={classes.row}>
            <CoverageCard overview={overview.data} />
            <SetupCard setup={overview.data.setup} />
          </div>
          <FixCards overview={overview.data} measures={measures.data} />
          <WorkInTeams overview={overview.data} />
        </>
      )}
    </div>
  );
};
